import { Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DialogLoadingButton } from '../../../component/dialog/DialogLoadingButton';
import { DatePickerControl } from '../../../component/form';
import { useBenefitContext } from '../BenefitContext';
import { PauseMutationDTO } from './useBulkPauseActivationsMutation';

interface Props {
  onUpdate: (update: PauseMutationDTO) => Promise<void>;
  terminationDate?: DateTime;
  progress?: number;
  disabled?: boolean;
}

interface FormValues {
  endCurrentActivation?: Date;
  startNewActivation?: Date;
}

export const PauseForm: React.FC<Props> = ({ onUpdate, terminationDate, progress, disabled }) => {
  const { t } = useTranslation('benefitsModule');
  const { benefit } = useBenefitContext();

  const maxDate = (terminationDate ? terminationDate.endOf('month') : DateTime.now().plus({ years: 10 })).toJSDate();

  const { control, setValue, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: {
      endCurrentActivation: DateTime.now().toJSDate(),
    },
  });

  const handleSave = useCallback(
    async (values: FormValues) => {
      await onUpdate({
        startNewActivation: DateTime.fromJSDate(values.startNewActivation!).startOf('month').plus({ months: 1 }).toFormat('yyyy-MM-dd'),
        endCurrentActivation: DateTime.fromJSDate(values.endCurrentActivation!).endOf('month').minus({ months: 1 }).toFormat('yyyy-MM-dd'),
      });
    },
    [onUpdate],
  );

  const endCurrentActivation = useWatch({ control, name: 'endCurrentActivation' });
  useEffect(() => {
    if (endCurrentActivation) {
      setValue('startNewActivation', DateTime.fromJSDate(endCurrentActivation).endOf('month').plus({ months: 1 }).toJSDate());
    }
  }, [endCurrentActivation, setValue]);

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <Stack spacing={4}>
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          <Trans i18nKey={[`benefitsModule:${benefit}.bulkEditDialog.pauseForm.text`, 'benefitsModule:bulkEditDialog.pauseForm.text']} />
        </Typography>
        <Stack
          spacing={1}
          direction="row"
          sx={{
            justifyContent: 'flex-start',
          }}
        >
          <DatePickerControl
            required
            monthYearPicker
            control={control}
            name="endCurrentActivation"
            label={t('labelStartDate')}
            minDate={DateTime.now().minus({ months: 1 }).endOf('month').toJSDate()}
            maxDate={maxDate}
            data-test-id="pauseForm-startDate"
          />
          <DatePickerControl
            required
            monthYearPicker
            control={control}
            name="startNewActivation"
            label={t('labelEndDate')}
            minDate={endCurrentActivation ? DateTime.fromJSDate(endCurrentActivation).toJSDate() : DateTime.now().endOf('month').toJSDate()}
            maxDate={maxDate}
            data-test-id="pauseForm-endDate"
          />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: 'flex-end',
          mt: 3,
        }}
      >
        <DialogLoadingButton
          type="submit"
          color="primary"
          loading={formState.isSubmitting}
          loadingVariant="determinate"
          loadingValue={progress}
          disabled={disabled}
          data-test-id="pauseForm-confirmButton"
        >
          {t('common:buttons.confirm')}
        </DialogLoadingButton>
      </Stack>
    </form>
  );
};
